'use strict';

/**
 * Build CTA rules – front-end evaluator
 * This module evaluates whether to show/hide the Build CTA button
 * based on product data and viewer context.
 */

/**
 * Extract viewer context from URL parameters
 * @returns {Object} { isDealer, isUpdateOrder }
 */
function getViewerContextFromURL() {
    const params = new URLSearchParams(window.location.search);
    const isDealer = !!(params.get('userId') && params.get('sessionId'));
    const isUpdateOrder =
        !!params.get('orderId') ||
        params.get('action') === 'Changeconfigurationtonewoffer' ||
        params.get('action') === 'changeVIN' ||
        !!params.get('modifyCar') ||
        !!params.get('modifyAccessories');
    return { isDealer, isUpdateOrder };
}

/**
 * Resolve master-level Hide Build CTA from several sources:
 * - hidden input #modelhidebuildcta (value: 'true'|'false')
 * - product.custom.hideBuildCTA === true
 * - product.customProps.hideBuildCTA === true
 * @param {Object} productData - Product data object
 * @returns {boolean} Whether to hide the Build CTA at master level
 */
function resolveMasterHide(productData) {
    const hiddenEl = document.getElementById('modelhidebuildcta');
    if (hiddenEl && typeof hiddenEl.value === 'string') {
        return hiddenEl.value === 'true';
    }
    const pd = (productData && productData.product)
        ? productData.product
        : productData;
    if (pd && pd.custom && pd.custom.hideBuildCTA === true) {
        return true;
    }
    if (pd && pd.customProps && pd.customProps.hideBuildCTA === true) {
        return true;
    }
    return false;
}

/**
 * Collect per-variant flags from variationAttributes[].values[].
 * (If server doesn't send dealerViewOnly, checks no-op.)
 * @param {Object} productData - Product data object
 * @returns {Object} { anyDealerOnly, anyDiscontinued, allDealerOnly, allDiscontinued, allNonDiscontinuedAreDealerOnly }
 */
function collectVariantFlags(productData) {
    const pd = productData && productData.product ? productData.product : productData;
    const attrs = (pd && pd.variationAttributes) ? pd.variationAttributes : [];
    const values = [];
    attrs.forEach(a => (a && a.values || []).forEach(v => values.push(v)));

    const safeSelectableValues = values.filter(v => v && v.selectable !== false);
    const anyDealerOnly = values.some(v => v && v.isLowStock === true);
    const anyDiscontinued = values.some(v => v && v.discontinued === true);
    const anyAvailable = values.some(v => v && v.discontinued !== true);

    // Count only selectable dealer-only variants
    const selectableDealerOnlyCount = safeSelectableValues.filter(v => v && v.isLowStock === true).length;
    const allDealerOnly = safeSelectableValues.length > 0 && selectableDealerOnlyCount >= safeSelectableValues.length;

    // NEW: Calculate for Rule 6 - check if all variants are discontinued
    const selectableDiscontinuedCount = safeSelectableValues.filter(v => v && v.discontinued === true).length;
    const allDiscontinued = safeSelectableValues.length > 0 && selectableDiscontinuedCount >= safeSelectableValues.length;

    // NEW: Calculate for Rule 8 - check if all NON-DISCONTINUED variants are dealer-only
    const nonDiscontinuedValues = safeSelectableValues.filter(v => v && v.discontinued !== true);
    const nonDiscontinuedDealerOnlyCount = nonDiscontinuedValues.filter(v => v && v.isLowStock === true).length;
    const allNonDiscontinuedAreDealerOnly = nonDiscontinuedValues.length > 0 &&
                                            nonDiscontinuedDealerOnlyCount >= nonDiscontinuedValues.length;

    return {
        anyDealerOnly,
        anyDiscontinued,
        allDealerOnly,
        allDiscontinued,
        allNonDiscontinuedAreDealerOnly,
        anyAvailable
    };
}

/**
 * Evaluate (Rules 1..8) per viewer
 * @param {Object} productData - Product data object
 * @param {Object} ctx - Viewer context { isDealer, isUpdateOrder }
 * @returns {Object} { showBuildCTA, allowConfigurator, filterDealerOnly, filterDiscontinued }
 */
function evaluateBuildCTAFront(productData, ctx) {
    const { isDealer, isUpdateOrder } = ctx;
    const masterHide = resolveMasterHide(productData);
    const { anyDealerOnly, anyDiscontinued, allDealerOnly, allDiscontinued, allNonDiscontinuedAreDealerOnly } = collectVariantFlags(productData);

    const result = {
        showBuildCTA: true,
        allowConfigurator: true,
        filterDealerOnly: false,
        filterDiscontinued: false
    };

    // R1: Hide Build CTA = No/NULL AND Dealer View Only = No/NULL AND Discontinued Flag = No/NULL
    // Build CTA visible for all (Customer & Dealer), Configurator visible and accessible for all
    if (!masterHide && !anyDealerOnly && !anyDiscontinued) return result;

    // R2: Hide Build CTA = Yes AND Dealer View Only = No/NULL AND Discontinued Flag = No/NULL (UPDATED)
    // Build CTA hidden for all (Customer & Dealer)
    // Configurator blocked for New Order, allowed for Update Order
    if (masterHide && !anyDealerOnly && !anyDiscontinued) {
        result.showBuildCTA = false;
        result.allowConfigurator = !!isUpdateOrder;  // Allow Update Order
        return result;
    }

    // R3: Hide Build CTA = No/NULL AND Dealer View Only = Yes AND Discontinued Flag = No/NULL (UPDATED)
    // Build CTA visible for all, Configurator visible for all but filters Dealer View Only variants for Customer
    // If all variants are Dealer View Only: Build CTA hidden for Customer, Configurator allowed for Update Order
    if (!masterHide && anyDealerOnly && !anyDiscontinued) {
        if (!isDealer && allDealerOnly) {
            result.showBuildCTA = false;
            result.allowConfigurator = !!isUpdateOrder;  // Allow Update Order for Customer
            return result;
        }
        result.filterDealerOnly = !isDealer;
        return result;
    }

    // R4: Hide Build CTA = Yes AND Dealer View Only = Yes AND Discontinued Flag = No/NULL (UPDATED)
    // When hideBuildCTA = Yes at master level, Customer is blocked for New Order
    // but allowed for Update Order.
    if (masterHide && anyDealerOnly && !anyDiscontinued) {
        result.showBuildCTA = isDealer;
        result.allowConfigurator = isDealer || !!isUpdateOrder;  // Allow Update Order
        result.filterDealerOnly = !isDealer;
        return result;
    }

    // R5: Hide Build CTA = Yes AND Dealer View Only = No/NULL AND Discontinued Flag = Yes
    // Build CTA hidden for all (Customer & Dealer) for New Order
    // Build CTA visible for all for Update Order
    // Configurator not accessible for New Order, accessible only for Update Order scenarios
    // Don't filter discontinued for Update Order - they need to see their existing config and accessories
    if (masterHide && anyDiscontinued && !anyDealerOnly) {
        result.showBuildCTA = false;
        result.allowConfigurator = !!isUpdateOrder;
        result.filterDiscontinued = !isUpdateOrder;  // Don't filter for Update Order
        return result;
    }

    // R6: Hide Build CTA = No/NULL AND Dealer View Only = No/NULL AND Discontinued Flag = Yes (UPDATED)
    // Build CTA visible for all (Customer & Dealer)
    // Configurator visible for all, only discontinued variants are filtered/hidden
    // If all variants are discontinued: Build CTA hidden, Configurator allowed for Update Order
    // Don't filter discontinued for Update Order - they need to see their existing config and accessories
    if (!masterHide && anyDiscontinued && !anyDealerOnly) {
        result.filterDiscontinued = !isUpdateOrder;  // Don't filter for Update Order

        // If ALL variants are discontinued, hide Build CTA but allow Update Order
        if (allDiscontinued) {
            result.showBuildCTA = false;
            result.allowConfigurator = !!isUpdateOrder;  // Allow Update Order
        }

        return result;
    }

    // R7: Hide Build CTA = Yes AND Dealer View Only = Yes AND Discontinued Flag = Yes
    // When hideBuildCTA = true at master level:
    // - If ALL variants are dealer-only AND ALL variants are discontinued:
    //   Hide build button on 9-grid, configurator not accessible for new orders (customer & dealer)
    // - If ANY variant is not discontinued:
    //   Allow dealer for new order, configurator accessible for dealer
    if (masterHide && allDealerOnly && anyDiscontinued) {
        // If ALL variants are discontinued: block everyone for new orders
        if (allDiscontinued) {
            result.showBuildCTA = false;
            result.allowConfigurator = !!isUpdateOrder;  // Only allow Update Order
            result.filterDealerOnly = !isDealer;
            result.filterDiscontinued = !isUpdateOrder;
            return result;
        }

        // If ANY variant is NOT discontinued: allow dealer for new orders
        result.showBuildCTA = isDealer;
        result.allowConfigurator = isDealer || !!isUpdateOrder;
        result.filterDealerOnly = !isDealer;
        result.filterDiscontinued = !isUpdateOrder;
        return result;
    }

    // R8: Hide Build CTA = No/NULL AND Dealer View Only = Yes AND Discontinued Flag = Yes (UPDATED)
    // Filters discontinued and dealer-only variants
    // If ALL variants discontinued: Hide Build CTA, allow Update Order
    // If all NON-DISCONTINUED are dealer-only: Hide for Customer for New Order, allow Update Order
    // Don't filter discontinued for Update Order - they need to see their existing config and accessories
    if (!masterHide && anyDealerOnly && anyDiscontinued) {
        result.filterDiscontinued = !isUpdateOrder;  // Don't filter for Update Order
        result.filterDealerOnly = !isDealer;

        // Calculate non-discontinued count
        const pd = productData && productData.product ? productData.product : productData;
        const attrs = (pd && pd.variationAttributes) ? pd.variationAttributes : [];
        const values = [];
        attrs.forEach(a => (a && a.values || []).forEach(v => values.push(v)));
        const safeSelectableValues = values.filter(v => v && v.selectable !== false);
        const nonDiscontinuedValues = safeSelectableValues.filter(v => v && v.discontinued !== true);

        // If ALL variants are discontinued, hide Build CTA but allow Update Order
        if (nonDiscontinuedValues.length === 0) {
            result.showBuildCTA = false;
            result.allowConfigurator = !!isUpdateOrder;  // Allow Update Order
            return result;
        }

        // If all NON-DISCONTINUED variants are dealer-only, hide for Customer but allow Update Order
        if (!isDealer && allNonDiscontinuedAreDealerOnly) {
            result.showBuildCTA = false;
            result.allowConfigurator = !!isUpdateOrder;  // Allow Update Order for Customer
        }
        return result;
    }

    return result;
}

// Store the current decision flags (module-level variable instead of window)
var currentBuildCTAFlags = null;

/**
 * Store the build CTA decision flags
 * @param {Object} decision - Decision object with flags
 */
function setFlags(decision) {
    currentBuildCTAFlags = decision;
}

/**
 * Get the current build CTA decision flags
 * @returns {Object|null} Current decision flags
 */
function getFlags() {
    return currentBuildCTAFlags;
}

/**
 * Get product data from DOM
 * @returns {Object|null} Product data object or null if not found
 */
function getProductDataFromDOM() {
    try {
        var summaryDataJSON = $('.js-save-summary').attr('data-car-summary-data');
        if (summaryDataJSON) {
            var dataObj = JSON.parse(summaryDataJSON);
            return dataObj.data || dataObj;
        }
    } catch (e) {
        console.warn('Failed to parse product data from DOM:', e);
    }
    return null;
}

/**
 * Evaluate and apply Build CTA rules from DOM data
 * @returns {Object|null} Decision object or null if data not available
 */
function evaluateFromDOM() {
    var productData = getProductDataFromDOM();
    if (!productData) {
        return null;
    }

    var viewerCtx = getViewerContextFromURL();
    return evaluateBuildCTAFront(productData, viewerCtx);
}

module.exports = {
    evaluateBuildCTAFront: evaluateBuildCTAFront,
    evaluateFromDOM: evaluateFromDOM,
    getProductDataFromDOM: getProductDataFromDOM,
    getViewerContextFromURL: getViewerContextFromURL,
    resolveMasterHide: resolveMasterHide,
    collectVariantFlags: collectVariantFlags,
    setFlags: setFlags,
    getFlags: getFlags
};
